import { h, Host } from "@stencil/core";
// import { fromGxImageToURL } from "../tree-view/genexus-implementation";
import { ACTION_MENU_ITEM_EXPORT_PARTS } from "../../common/reserved-names";
import { SyncWithRAF } from "../../common/sync-with-frames";
import { MARKER_CLASS_SELECTOR, renderItems } from "./renders";
// const FLOATING_POINT_ERROR = 1;
const INTERSECTION_OPTIONS = { threshold: 1 };
export class ChActionGroupRender {
    constructor() {
        this.collapsedItems = 0;
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.gxImageConstructor = undefined;
        this.gxSettings = undefined;
        this.itemsOverflowBehavior = "responsive-collapse";
        this.model = undefined;
        this.moreActionsAccessibleName = "Show more actions";
        this.moreActionsBlockAlign = "outside-end";
        this.moreActionsCaption = undefined;
        this.moreActionsInlineAlign = "inside-start";
        this.useGxRender = false;
    }
    #collapsedModel = [];
    #isResponsiveCollapse = false;
    #shouldCheckResponsiveCollapseWatcher = true;
    // Responsive collapse variables
    #displayedMarkers;
    #responsiveActionsWatcher;
    #updateActionsRAF; // Don't allocate memory until needed when dragging
    collapsedItemsChanged() {
        this.#setModels();
    }
    el;
    itemsOverflowBehaviorChanged() {
        this.#shouldCheckResponsiveCollapseWatcher = true;
        this.#isResponsiveCollapse =
            this.itemsOverflowBehavior === "responsive-collapse";
        this.#disconnectActionsObserver();
        this.#removeOrInitializeMarkersVisibility();
    }
    modelChanged() {
        this.#shouldCheckResponsiveCollapseWatcher = true;
        this.#disconnectActionsObserver();
        this.#removeOrInitializeMarkersVisibility();
        this.#setModels();
    }
    // TODO: Use the getImagePath for resolving the images
    // #getImagePath = (img: string) =>
    //   this.useGxRender
    //     ? fromGxImageToURL(img, this.gxSettings, this.gxImageConstructor)
    //     : img;
    #removeOrInitializeMarkersVisibility = () => {
        this.#displayedMarkers = this.#isResponsiveCollapse
            ? this.model?.map((_, index) => ({
                id: index.toString(),
                displayed: true
            })) ?? []
            : undefined;
    };
    #setModels = () => {
        this.#collapsedModel = [];
        if (this.model && this.collapsedItems > 0) {
            this.#collapsedModel = this.model.slice(this.model.length - this.collapsedItems);
        }
    };
    // - - - - - - - - - - - - - - - - - - - -
    //                Observers
    // - - - - - - - - - - - - - - - - - - - -
    #setResponsiveCollapse = () => {
        if (this.#isResponsiveCollapse) {
            this.#connectActionsObserver();
        }
    };
    #connectActionsObserver = () => {
        this.#updateActionsRAF ??= new SyncWithRAF();
        this.#responsiveActionsWatcher ??= new IntersectionObserver(entries => {
            // Update the visibility of each entry
            entries.forEach(entry => {
                const itemId = Number(entry.target.id);
                if (this.model[itemId].type === "slot") {
                    this.#displayedMarkers[itemId].size = `${entry.target.offsetWidth}px`;
                }
                this.#displayedMarkers[itemId].displayed = entry.isIntersecting;
            });
            // Queue a task to update the displayed actions in the next frame
            this.#updateActionsRAF.perform(this.#updateDisplayedActions);
        }, INTERSECTION_OPTIONS);
        // Observe the actions
        this.el.shadowRoot
            .querySelectorAll(MARKER_CLASS_SELECTOR)
            .forEach(action => this.#responsiveActionsWatcher.observe(action));
    };
    /**
     * Update the visibility of the actions.
     * Only works if itemsOverflowBehavior === "responsive-collapse"
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #updateDisplayedActions = () => {
        const firstItemIndexThatIsNotVisible = this.#displayedMarkers.findIndex(markerIsDisplayed => !markerIsDisplayed.displayed);
        // All items are visible
        if (firstItemIndexThatIsNotVisible === -1) {
            this.collapsedItems = 0;
        }
        // There are hidden items
        else {
            this.collapsedItems = this.model.length - firstItemIndexThatIsNotVisible;
        }
    };
    #disconnectActionsObserver = () => {
        this.#updateActionsRAF?.cancel();
        this.#updateActionsRAF = undefined;
        this.#responsiveActionsWatcher?.disconnect();
        this.#responsiveActionsWatcher = undefined;
    };
    connectedCallback() {
        // TODO: Use role="menu"
        this.el.setAttribute("role", "list");
        this.collapsedItems = 0;
        this.#isResponsiveCollapse =
            this.itemsOverflowBehavior === "responsive-collapse";
        this.#removeOrInitializeMarkersVisibility();
        this.#setModels();
    }
    componentDidRender() {
        if (this.#shouldCheckResponsiveCollapseWatcher) {
            this.#shouldCheckResponsiveCollapseWatcher = false;
            this.#setResponsiveCollapse();
        }
    }
    disconnectedCallback() {
        this.#disconnectActionsObserver();
    }
    render() {
        if (!this.model || this.model.length === 0) {
            return "";
        }
        return (h(Host, null, this.#isResponsiveCollapse && this.collapsedItems !== 0 && (h("ch-action-menu-render", { key: "__action-menu", role: "listitem", exportparts: ACTION_MENU_ITEM_EXPORT_PARTS, blockAlign: this.moreActionsBlockAlign, disabled: this.disabled, getImagePathCallback: this.getImagePathCallback, inlineAlign: this.moreActionsInlineAlign, model: this.#collapsedModel }, this.moreActionsCaption, this.#collapsedModel.map(item => item.type === "slot" ? (h("slot", { slot: item.id, name: item.id })) : undefined))), renderItems(this.model, this.#isResponsiveCollapse, this.#displayedMarkers, this.disabled, this.getImagePathCallback)));
        // const thereAreCollapsedItems =
        //   this.itemsOverflowBehavior === "responsive-collapse" &&
        //   this.moreActionsButtonWasExpanded &&
        //   this.model != null &&
        //   this.displayedItemsCount !== -1;
        // return (
        //   <ch-action-group
        //     exportparts={this.actionGroupExportParts}
        //     itemsOverflowBehavior={this.itemsOverflowBehavior}
        //     moreActionsAccessibleName={this.moreActionsAccessibleName}
        //     // moreActionsDropdownPosition={this.moreActionsDropdownPosition}
        //     openOnFocus={this.openOnFocus}
        //     onDisplayedItemsCountChange={this.#handleDisplayedItemsCountChange}
        //     onMoreActionsButtonExpandedChange={
        //       !this.moreActionsButtonWasExpanded
        //         ? this.#handleMoreActionButtonExpandedChange
        //         : null
        //     }
        //   >
        //     {this.model.map(this.#renderItem)}
        //     {/* {this.model != null &&
        //       this.model.map((item, index) => (
        //         <ch-action-group-item
        //           slot="items"
        //           key={item.id || item.caption || index}
        //         >
        //           {this.#firstLevelRenderItem(item, index, 0)}
        //         </ch-action-group-item>
        //       ))} */}
        //     {/* {thereAreCollapsedItems &&
        //       this.model
        //         .filter((_, index) => index >= this.displayedItemsCount)
        //         .map(this.#firstLevelRenderCollapsedItem(0))} */}
        //   </ch-action-group>
        // );
    }
    static get is() { return "ch-action-group-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["action-group-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-group-render.css"]
        };
    }
    static get properties() {
        return {
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionMenuImagePathCallback",
                    "resolved": "(item: ActionMenuItemActionableModel, iconDirection: \"start\" | \"end\") => GxImageMultiState",
                    "references": {
                        "ActionMenuImagePathCallback": {
                            "location": "import",
                            "path": "../action-menu/types",
                            "id": "src/components/action-menu/types.ts::ActionMenuImagePathCallback"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc or endImgSrc (of an item) needs to be resolved."
                }
            },
            "gxImageConstructor": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(name: string) => any",
                    "resolved": "(name: string) => any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                }
            },
            "gxSettings": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "gx-settings",
                "reflect": false
            },
            "itemsOverflowBehavior": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ItemsOverflowBehavior",
                    "resolved": "\"add-scroll\" | \"multiline\" | \"responsive-collapse\"",
                    "references": {
                        "ItemsOverflowBehavior": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-group/types.ts::ItemsOverflowBehavior"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute determines how items behave when the content of the ActionGroup overflows horizontal. This property is needed\nto make the control responsive to changes in the Width of the container of ActionGroup.\n\n| Value                 | Details                                                                                          |\n| --------------------- | ------------------------------------------------------------------------------------------------ |\n| `add-scroll`          | The items of the ActionGroup that overflow horizontally are shown by means of a scroll.          |\n| `multiline`           | The ActionGroup items that overflow horizontally are shown in a second line of the control.      |\n| `responsive-collapse` | The Action Group items, when they start to overflow the control, are placed in the More Actions. |"
                },
                "attribute": "items-overflow-behavior",
                "reflect": true,
                "defaultValue": "\"responsive-collapse\""
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionGroupModel | undefined",
                    "resolved": "ActionGroupItemModel[]",
                    "references": {
                        "ActionGroupModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-group/types.ts::ActionGroupModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the model of the ch-action-group control."
                }
            },
            "moreActionsAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you specify the label for the more actions button.\nImportant for accessibility."
                },
                "attribute": "more-actions-accessible-name",
                "reflect": false,
                "defaultValue": "\"Show more actions\""
            },
            "moreActionsBlockAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the block alignment of the more actions dropdown that is\nplaced relative to the \"more actions\" button."
                },
                "attribute": "more-actions-block-align",
                "reflect": false,
                "defaultValue": "\"outside-end\""
            },
            "moreActionsCaption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the caption for the more actions button."
                },
                "attribute": "more-actions-caption",
                "reflect": false
            },
            "moreActionsInlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the more actions dropdown that is\nplaced relative to the \"more actions\" button."
                },
                "attribute": "more-actions-inline-align",
                "reflect": false,
                "defaultValue": "\"inside-start\""
            },
            "useGxRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "use-gx-render",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "collapsedItems": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "collapsedItems",
                "methodName": "collapsedItemsChanged"
            }, {
                "propName": "itemsOverflowBehavior",
                "methodName": "itemsOverflowBehaviorChanged"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }];
    }
}
